import React, { useEffect, useState } from 'react';
import { getMyStartup, updateMyStartup, fetchApi, checkSiteUrl } from '../../api';
import { useToast } from '../../components/Toast';
import { REFERRAL_SOURCES, migrateLegacyReferral, parseReferralValue, buildReferralValue } from '../../utils/referralSources';

const SECTEURS = ['Fintech', 'Insurtech', 'Regtech', 'Biotech', 'Greentech', 'Medtech', 'SaaS', 'Immotech', 'HealthTech', 'Industrie 4.0'];
const STADES = ['Pré-amorçage', 'Amorçage', 'Série A', 'Série B', 'Série C+'];

// Chiffre d'affaires : table de correspondance [valeur stockée, libellé affiché].
// On CONSERVE l'ancienne valeur en base (utilisée par le filtre de recherche et
// affichée telle quelle sur les autres écrans) — seul le libellé du menu déroulant
// de ce champ change. Aucune migration de données requise.
const CA_OPTIONS = [
  ['< 500 k€', 'Moins de 500 k€'],
  ['500k–2M€', '500k–2M€'],
  ['2M–10M€', '2M–10M€'],
  ['> 10M€', 'Plus de 10 M€'],
];

export default function StartupProfile() {
  const [startup, setStartup] = useState(null);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [lookingUp, setLookingUp] = useState(false);
  const [lookupResult, setLookupResult] = useState(null);
  const [summarizing, setSummarizing] = useState(false);
  const [urlCheck, setUrlCheck] = useState({ checking: false, result: null });

  const normalizeSiteWeb = (raw) => {
    if (!raw) return raw;
    let url = raw.trim();
    const localMatch = url.match(/^https?:\/\/localhost[:\d]*\/(?:startup|startups|detail)\/?(.+)/i);
    if (localMatch) url = localMatch[1];
    if (/^https?:\/\/localhost/i.test(url)) return '';
    if (!/^https?:\/\//i.test(url) && url.includes('.')) {
      if (!url.startsWith('www.')) url = 'www.' + url;
      url = 'https://' + url;
    }
    return url;
  };

  const handleSiteWebBlur = () => {
    const normalized = normalizeSiteWeb(form.site_web);
    if (normalized !== form.site_web) upd('site_web', normalized);
    setUrlCheck({ checking: false, result: null });
  };

  const handleCheckUrl = async () => {
    if (!form.site_web) return;
    setUrlCheck({ checking: true, result: null });
    try {
      const res = await checkSiteUrl(form.site_web);
      setUrlCheck({ checking: false, result: res });
      if (res.normalized_url && res.normalized_url !== form.site_web) upd('site_web', res.normalized_url);
    } catch (err) {
      setUrlCheck({ checking: false, result: { accessible: false, reason: err.message } });
    }
  };
  const [summaryError, setSummaryError] = useState(null);
  const toast = useToast();

  useEffect(() => {
    getMyStartup().then(s => { setStartup(s); setForm(s); }).catch(console.error);
  }, []);

  const upd = (k, v) => { setForm(f => ({ ...f, [k]: v })); setSaved(false); };

  const handleSave = async () => {
    setSaving(true);
    try {
      await updateMyStartup(form);
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch (err) { toast.error(err.message); }
    setSaving(false);
  };

  const handleSiretLookup = async () => {
    const siret = (form.siret || '').replace(/[^0-9]/g, '');
    if (siret.length < 9) { toast.error('Le SIRET doit contenir au moins 9 chiffres'); return; }
    setLookingUp(true);
    setLookupResult(null);
    try {
      // Search by SIRET directly (SIREN = first 9 digits)
      const res = await fetchApi(`/siret/search?q=${siret.substring(0, 9)}`);
      if (res.results && res.results.length > 0) {
        // Find the exact match by SIRET or take first
        const match = res.results.find(r => r.siret === siret) || res.results[0];
        setLookupResult(match);
        // Auto-fill fields
        if (match.adresse) upd('adresse', match.adresse);
        if (match.siren) upd('siren', match.siren);
        if (match.siret && !form.siret) upd('siret', match.siret);
        if (match.activite_principale) upd('activite_principale', match.activite_principale);
        if (match.date_creation) {
          const year = parseInt(match.date_creation.substring(0, 4));
          if (year > 1900) upd('annee_creation', year);
        }
        if (match.dirigeants && match.dirigeants.length > 0) {
          const dirigeantStr = match.dirigeants.map(d => `${d.prenoms || ''} ${d.nom || ''} (${d.qualite || ''})`).join(', ');
          upd('fondateurs', dirigeantStr);
        }
        if (match.tranche_effectif) {
          // Map tranche to number
          const tranches = {'0':0,'1-2':2,'3-5':4,'6-9':8,'10-19':15,'20-49':35,'50-99':75,'100-199':150,'200-249':225,'250-499':375,'500-999':750,'1000-1999':1500,'2000-4999':3500,'5000-9999':7500};
          const eff = tranches[match.tranche_effectif];
          if (eff) upd('effectif', eff);
        }
      } else {
        setLookupResult({ not_found: true });
      }
    } catch (err) { toast.error('Erreur de recherche : ' + err.message); }
    setLookingUp(false);
  };

  const handleAutoSummary = async () => {
    if (!form.site_web) { toast.error('Renseignez d\'abord le site web'); return; }
    setSummarizing(true);
    setSummaryError(null);
    try {
      const res = await fetchApi('/websummary/generate', {
        method: 'POST',
        body: JSON.stringify({ url: form.site_web, startup_name: form.name || '' })
      });
      if (res.success && res.summary) {
        upd('pitch', res.summary);
        // Also save immediately
        if (startup?.id) {
          await fetchApi('/websummary/save', {
            method: 'POST',
            body: JSON.stringify({ startup_id: startup.id, pitch: res.summary })
          });
        }
      } else {
        setSummaryError(res.reason || 'Impossible de générer un résumé');
      }
    } catch (err) {
      setSummaryError(err.message);
    }
    setSummarizing(false);
  };

  if (!startup) return <div className="loading">Chargement du profil...</div>;

  return (
    <div style={{ padding: '32px 0' }}>
      <div className="container" style={{ maxWidth: 800 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
          <h1>Profil Startup</h1>
          <button onClick={handleSave} className={`btn ${saved ? 'btn-outline' : 'btn-accent'}`} disabled={saving}>
            {saving ? 'Sauvegarde...' : saved ? 'Sauvegardé !' : 'Enregistrer'}
          </button>
        </div>

        {startup.source === 'scraping' && (
          <div className="card" style={{ marginBottom: 24, borderLeft: '4px solid #2E75B6', background: '#F0F7FF' }}>
            <p style={{ fontSize: 13, color: 'var(--primary)' }}>
              Certaines données ont été pré-remplies par scraping (Pappers/Société.com). Les champs en lecture seule proviennent de sources officielles.
            </p>
          </div>
        )}

        <div className="card" style={{ marginBottom: 24 }}>
          <h3 style={{ marginBottom: 16 }}>Identité légale</h3>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
            <div>
              <label className="form-label">Raison sociale</label>
              <input type="text" value={form.name || ''} onChange={e => upd('name', e.target.value)} className="form-input" />
            </div>
            <div>
              <label className="form-label">Forme juridique</label>
              <select value={form.forme_juridique || 'SAS'} onChange={e => upd('forme_juridique', e.target.value)} className="form-input">
                {['SARL', 'SAS', 'SA', 'SCA', 'EURL', 'SASU'].map(f => <option key={f}>{f}</option>)}
              </select>
            </div>

            {/* SIRET with lookup button */}
            <div>
              <label className="form-label">SIRET</label>
              <div style={{ display: 'flex', gap: 8 }}>
                <input type="text" value={form.siret || ''} onChange={e => upd('siret', e.target.value.replace(/[^0-9]/g, '').substring(0, 14))}
                  className="form-input" placeholder="14 chiffres" style={{ flex: 1, fontFamily: 'monospace', letterSpacing: 1 }} />
                <button onClick={handleSiretLookup} className="btn btn-primary" disabled={lookingUp || !(form.siret && form.siret.replace(/[^0-9]/g, '').length >= 9)}
                  style={{ fontSize: 12, padding: '8px 14px', whiteSpace: 'nowrap' }}
                  title="Remplir automatiquement les informations légales à partir du SIRET">
                  {lookingUp ? '...' : 'Auto-remplir'}
                </button>
              </div>
            </div>
            <div>
              <label className="form-label">SIREN</label>
              <input type="text" value={form.siren || ''} onChange={e => upd('siren', e.target.value)} className="form-input"
                style={{ fontFamily: 'monospace', letterSpacing: 1 }} placeholder="9 chiffres" readOnly={!!lookupResult && !lookupResult.not_found} />
            </div>

            {/* Auto-filled fields */}
            <div style={{ gridColumn: '1 / -1' }}>
              <label className="form-label">Adresse du siège social</label>
              <input type="text" value={form.adresse || ''} onChange={e => upd('adresse', e.target.value)} className="form-input" />
            </div>
            <div>
              <label className="form-label">Activité (code NAF)</label>
              <input type="text" value={form.activite_principale || ''} onChange={e => upd('activite_principale', e.target.value)} className="form-input" placeholder="Ex: 6201Z" />
            </div>
            <div>
              <label className="form-label">Année de création</label>
              <input type="number" value={form.annee_creation || ''} onChange={e => upd('annee_creation', parseInt(e.target.value) || null)} className="form-input" placeholder="Ex: 2020" />
            </div>
            <div>
              <label className="form-label">Effectif</label>
              <input type="number" value={form.effectif || ''} onChange={e => upd('effectif', parseInt(e.target.value) || null)} className="form-input" />
            </div>
            <div>
              <label className="form-label">Dirigeant(s)</label>
              <input type="text" value={form.fondateurs || ''} onChange={e => upd('fondateurs', e.target.value)} className="form-input" placeholder="Nom et prénom du dirigeant" />
            </div>
            <div>
              <label className="form-label">Site web</label>
              <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                <input type="url" value={form.site_web || ''} onChange={e => { upd('site_web', e.target.value); setUrlCheck({ checking: false, result: null }); }} onBlur={handleSiteWebBlur} className="form-input" placeholder="https://www.monsite.com" style={{ flex: 1 }} />
                <button type="button" onClick={handleCheckUrl} disabled={!form.site_web || urlCheck.checking}
                  style={{ padding: '8px 12px', fontSize: 11, background: urlCheck.result?.accessible ? '#4CAF50' : 'var(--primary)', color: '#fff', border: 'none', borderRadius: 6, cursor: 'pointer', whiteSpace: 'nowrap', opacity: (!form.site_web || urlCheck.checking) ? 0.5 : 1 }}>
                  {urlCheck.checking ? '⏳' : urlCheck.result?.accessible ? '✓ OK' : '🔗 Tester'}
                </button>
                {/* Bouton "Générer présentation" désactivé sur demande */}
              </div>
              {urlCheck.result && (
                <div style={{ marginTop: 4, fontSize: 11, color: urlCheck.result.accessible ? '#2E7D32' : '#C62828', padding: '4px 8px', background: urlCheck.result.accessible ? '#E8F5E9' : '#FFEBEE', borderRadius: 4 }}>
                  {urlCheck.result.accessible ? `✓ Site accessible (${urlCheck.result.status_code})` : `✗ ${urlCheck.result.reason || 'Site inaccessible'}`}
                </div>
              )}
            </div>
          </div>

          {/* Lookup result feedback */}
          {lookupResult && !lookupResult.not_found && (
            <div style={{ marginTop: 16, padding: 12, background: '#E8F5E9', borderRadius: 8, fontSize: 13 }}>
              <strong style={{ color: '#2E7D32' }}>Informations récupérées depuis le RCS</strong>
              <div style={{ marginTop: 6, color: 'var(--text-light)' }}>
                <strong>{lookupResult.nom_complet}</strong> — SIREN {lookupResult.siren}
                {lookupResult.etat_administratif === 'A' ?
                  <span className="badge badge-vert" style={{ fontSize: 10, marginLeft: 8 }}>Active</span> :
                  <span className="badge badge-rouge" style={{ fontSize: 10, marginLeft: 8 }}>Fermée</span>
                }
              </div>
            </div>
          )}
          {lookupResult && lookupResult.not_found && (
            <div style={{ marginTop: 16, padding: 12, background: '#FFF3E0', borderRadius: 8, fontSize: 13, color: '#E65100' }}>
              Aucune entreprise trouvée pour ce SIRET. Vérifiez le numéro saisi.
            </div>
          )}
        </div>

        <div className="card" style={{ marginBottom: 24 }}>
          <h3 style={{ marginBottom: 16 }}>Informations métier</h3>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
            <div>
              <label className="form-label">Secteur d'activité</label>
              <select value={form.secteur || ''} onChange={e => upd('secteur', e.target.value)} className="form-input">
                {SECTEURS.map(s => <option key={s}>{s}</option>)}
              </select>
            </div>
            <div>
              <label className="form-label">Sous-domaine</label>
              <input type="text" value={form.sous_domaine || ''} onChange={e => upd('sous_domaine', e.target.value)} className="form-input" />
            </div>
            <div>
              <label className="form-label">Stade de développement</label>
              <select value={form.stade_dev || ''} onChange={e => upd('stade_dev', e.target.value)} className="form-input">
                <option value="">-</option>
                {STADES.map(s => <option key={s}>{s}</option>)}
              </select>
            </div>
            <div>
              <label className="form-label">Maturité</label>
              <select value={form.maturite || ''} onChange={e => upd('maturite', e.target.value)} className="form-input">
                {['< 6 mois', '< 12 mois', '< 18 mois', '< 24 mois', '< 36 mois', '> 36 mois'].map(m => <option key={m}>{m}</option>)}
              </select>
            </div>
            <div>
              <label className="form-label">Chiffre d'affaires</label>
              <select value={form.ca || ''} onChange={e => upd('ca', e.target.value)} className="form-input">
                {CA_OPTIONS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
              </select>
            </div>
            <div>
              <label className="form-label">Clientèle</label>
              <select value={form.adressage_client || ''} onChange={e => upd('adressage_client', e.target.value)} className="form-input">
                <option value="B2B">B2B</option><option value="B2C">B2C</option><option value="B2B2C">B2B2C</option>
              </select>
            </div>
            <div>
              <label className="form-label">Région</label>
              <input type="text" value={form.region || ''} onChange={e => upd('region', e.target.value)} className="form-input" />
            </div>
          </div>
        </div>

        <div className="card" style={{ marginBottom: 24 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
            <h3 style={{ margin: 0 }}>Présentation</h3>
            {/* Bouton "Générer depuis le site web" désactivé sur demande */}
          </div>
          <label className="form-label">Présentation (1000 car. max)</label>
          <textarea value={form.pitch || ''} onChange={e => upd('pitch', e.target.value.slice(0, 1000))} className="form-input"
            rows={6} style={{ resize: 'vertical' }} maxLength={1000}
            placeholder="Décrivez votre startup en quelques lignes" />
          <div style={{ fontSize: 12, color: 'var(--text-light)', marginTop: 4 }}>{(form.pitch || '').length}/1000</div>
        </div>

        <div className="card">
          <h3 style={{ marginBottom: 16 }}>Contact</h3>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
            <div>
              <label className="form-label">Nom du contact</label>
              <input type="text" value={form.contact_name || ''} onChange={e => upd('contact_name', e.target.value)} className="form-input" />
            </div>
            <div>
              <label className="form-label">Email de contact</label>
              <input type="email" value={form.contact_email || ''} onChange={e => upd('contact_email', e.target.value)} className="form-input" />
            </div>
          </div>
        </div>

        <div className="card" style={{ marginTop: 24 }}>
          <h3 style={{ marginBottom: 16 }}>Informations complémentaires</h3>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
            <div>
              <label className="form-label">Comment avez-vous entendu parler de l'événement ?</label>
              {(() => {
                const migrated = migrateLegacyReferral(form.referral_source || '');
                const { option, otherText } = parseReferralValue(migrated);
                return (
                  <>
                    <select value={option} onChange={e => upd('referral_source', buildReferralValue(e.target.value, otherText))} className="form-input">
                      <option value="">-- Non renseigné --</option>
                      {REFERRAL_SOURCES.map(s => <option key={s} value={s}>{s}</option>)}
                    </select>
                    {option === 'Autre' && (
                      <input type="text" value={otherText}
                        onChange={e => upd('referral_source', buildReferralValue('Autre', e.target.value))}
                        placeholder="Précisez..."
                        className="form-input" style={{ marginTop: 8 }} />
                    )}
                  </>
                );
              })()}
            </div>
            <div>
              <label className="form-label">Consentement - Visibilité du score ISI</label>
              <select value={form.consent_score_visible === false ? 'false' : 'true'} onChange={e => upd('consent_score_visible', e.target.value === 'true')} className="form-input">
                <option value="true">Je consens à rendre visible mon score ISI auprès des investisseurs</option>
                <option value="false">Je ne consens pas à rendre visible mon score ISI aux investisseurs</option>
              </select>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
